// RepulpWasteEntry.jsx
import React, { useEffect, useState } from "react";
import {
  Grid, TextField, Box, Divider, Typography,
  MenuItem, Snackbar, Alert, Switch
} from "@mui/material";
import { toast } from "react-toastify";
import RepulpWasteActionMenu from "./RepulpWasteActionMenu";
import { callApi } from "../../../utils/apiClient";
import { Production_API_ENDPOINTS } from "../../../config/api/apiCallProduction";
import dayjs from "dayjs";
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const RepulpWasteEntry = ({ formData, setFormData, setIsFormOpen, loadList }) => {

  const [isEdit, setIsEdit] = useState(!!formData.repulp_entry_id);
  const [locations, setLocations] = useState([]);
  const [machineList, setMachineList] = useState([]);
  const [shiftList, setShiftList] = useState([]);
  const [users, setUsers] = useState([]);
  const [wasteTypes, setWasteTypes] = useState([]);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("warning");

  const showAlert = (msg, severity = "warning") => {
    setAlertMessage(msg);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  // ---------------------------------------------------------
  // API CALLS
  // ---------------------------------------------------------
  const loadMachines = async () => {
    try {
      const companyId = Number(localStorage.getItem("companyid") || 0);
      const plantId = Number(localStorage.getItem("plantid") || 1);
      const userId = Number(localStorage.getItem("userID") || 0);

      const payload = { p_company_id: companyId, p_plant_id: plantId, p_created_by: userId };
      const res = await callApi(
        Production_API_ENDPOINTS.repulp.machineList || "/api/repulp/machineList",
        "POST",
        { "Content-Type": "application/json" },
        payload
      );
      setMachineList(res || []);
    } catch (err) {
      console.error("Failed to load machines", err);
      showAlert("Failed to load machine list", "error");
    }
  };

  const loadLocations = async (machineId) => {
    try {
      const companyId = Number(localStorage.getItem("companyid") || 0);
      const plantId = Number(localStorage.getItem("plantid") || 1);
      const userId = Number(localStorage.getItem("userID") || 0);

      const payload = {
        p_company_id: companyId,
        p_plant_id: plantId,
        p_created_by: userId,
        p_machineid: machineId
      };

      const res = await callApi(
        Production_API_ENDPOINTS.repulp.locationList || "/api/repulp/locationList",
        "POST",
        { "Content-Type": "application/json" },
        payload
      );
      setLocations(res || []);
      handleChange("location_id", ""); // reset location if machine changes
    } catch (err) {
      console.error("Failed to load locations", err);
      showAlert("Failed to load locations", "error");
    }
  };

  const loadOtherDropdowns = async () => {
    try {
      const companyId = Number(localStorage.getItem("companyid") || 0);
      const plantId = Number(localStorage.getItem("plantid") || 1);
      const userId = Number(localStorage.getItem("userID") || 0);
      const payload = { p_company_id: companyId, p_plant_id: plantId, p_created_by: userId };

      // Waste Types
      const wasteRes = await callApi(
        Production_API_ENDPOINTS.repulp.WasteTypeList || "/api/repulp/WasteTypeList",
        "POST",
        { "Content-Type": "application/json" },
        payload
      );
      setWasteTypes(wasteRes || []);

      // Shifts
      const shiftRes = await callApi(
        Production_API_ENDPOINTS.repulp.shiftList || "/api/repulp/shiftList",
        "POST",
        { "Content-Type": "application/json" },
        payload
      );
      setShiftList(shiftRes || []);

      // Shift Incharges
      const usersRes = await callApi(
        Production_API_ENDPOINTS.repulp.shiftInchargeList || "/api/repulp/shiftInchargeList",
        "POST",
        { "Content-Type": "application/json" },
        payload
      );
      setUsers(usersRes || []);

    } catch (err) {
      console.error("Failed to load dropdowns", err);
      showAlert("Failed to load dropdown items", "error");
    }
  };

  // ---------------------------------------------------------
  // INITIAL LOAD
  // ---------------------------------------------------------
  useEffect(() => {
    loadMachines();
    loadOtherDropdowns();
    if (isEdit && formData.machine_id) {
      loadLocations(formData.machine_id);
    }
  }, []);

  // ---------------------------------------------------------
  // FORM HANDLERS
  // ---------------------------------------------------------
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Cascade: reload locations when machine changes
    if (field === "machine_id") {
      loadLocations(value);
    }
  };

  const validateFields = () => {
    if (!formData.entry_date) return showAlert("Entry Date is required");
    if (!formData.location_id) return showAlert("Location is required");
    if (!formData.machine_id) return showAlert("Machine is required");
    if (!formData.waste_type_id) return showAlert("Waste Type is required");
    if (!formData.shift_id) return showAlert("Shift Code is required");
    if (!formData.shift_incharge_id) return showAlert("Shift Incharge is required");
    if (!formData.quantity) return showAlert("Quantity is required");
    return true;
  };

  const handleSave = async () => {
    if (!validateFields()) return;

    try {
      const payload = {
        entry_date: formData.entry_date,
        stage: formData.stage || "",
        location_id: formData.location_id,
        machine_id: formData.machine_id,
        batch_no: formData.batch_no || "",
        reel_no: formData.reel_no || "",
        waste_type_id: formData.waste_type_id,
        quantity: Number(formData.quantity),
        uom: formData.uom || "KG",
        shift_id: formData.shift_id,
        shift_incharge_id: formData.shift_incharge_id,
        remarks: formData.remarks || "",
        status: formData.status === undefined ? true : formData.status,
        company_id: Number(localStorage.getItem("companyid")),
        plant_id: Number(localStorage.getItem("plantid") || 1),
        created_by: Number(localStorage.getItem("userID")),
      };

      const res = await callApi(
        Production_API_ENDPOINTS.repulp.create || "/api/repulp/create",
        "POST",
        { "Content-Type": "application/json" },
        payload
      );

      toast.success(res?.message || "Saved Successfully", { position: "bottom-center" });
      loadList();
      setIsEdit(true);

    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save", { position: "bottom-center" });
    }
  };

  const handleDelete = async () => {
    if (!formData.repulp_entry_id) return;

    try {
      const companyId = Number(localStorage.getItem("companyid") || 0);
      const plantId = Number(localStorage.getItem("plantid") || 1);
      const userId = Number(localStorage.getItem("userID") || 0);

      const payload = {
        p_company_id: companyId,
        p_plant_id: plantId,
        p_created_by: userId,
        p_entry_id: formData.repulp_entry_id,
        p_delete_type: 1,
      };

      await callApi(
        Production_API_ENDPOINTS.repulp.delete || "/api/repulp/delete",
        "POST",
        { "Content-Type": "application/json" },
        payload
      );

      toast.success("Entry deleted", { position: "bottom-center" });
      loadList();
      setIsFormOpen(false);

    } catch {
      toast.error("Failed to delete", { position: "bottom-center" });
    }
  };

  const handleClear = () => {
    setFormData({
      entry_date: "",
      location_id: "",
      machine_id: "",
      waste_type_id: "",
      shift_id: "",
      shift_incharge_id: "",
      batch_no: "",
      reel_no: "",
      quantity: "",
      uom: "KG",
      remarks: "",
      status: true,
    });
    setIsEdit(false);
  };

  // ---------------------------------------------------------
  // UI RENDER
  // ---------------------------------------------------------
  return (
    <>
      {/* Sticky Top Action Bar */}
      <Box sx={{ position: "sticky", top: 0, zIndex: 20, p: 1, mb: 1 }}>
        <RepulpWasteActionMenu
          isEdit={isEdit}
          onSave={handleSave}
          onClear={handleClear}
          onDelete={handleDelete}
          onExit={() => setIsFormOpen(false)}
          saveDisabled={!formData.entry_date || !formData.quantity}
        />
        <Divider />
      </Box>

      {/* Scrollable Fields */}
      <Box sx={{ padding: "5px", maxHeight: "75vh", overflowY: "auto" }}>
        <Box p={3}>
          <Grid container spacing={2}>

            {/* Entry Date */}
            <Grid item xs={8}>
              <Field label="Entry Date" required>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="Entry Date"
                    inputFormat="DD-MMM-YYYY"
                    value={formData.entry_date ? dayjs(formData.entry_date) : null}
                    onChange={(newValue) => handleChange("entry_date", dayjs(newValue).format("YYYY-MM-DD"))}
                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                  />
                </LocalizationProvider>
              </Field>
            </Grid>

            {/* Machine */}
            <Grid item xs={8}>
              <Field label="Machine" required>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={formData.machine_id || ""}
                  onChange={(e) => handleChange("machine_id", e.target.value)}
                >
                  {machineList.map((m) => (
                    <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                  ))}
                </TextField>
              </Field>
            </Grid>

            {/* Location */}
            <Grid item xs={8}>
              <Field label="Stage" required>
                <TextField
                  select
                  size="small"
                  fullWidth
                  value={formData.location_id || ""}
                  onChange={(e) => handleChange("location_id", e.target.value)}
                  disabled={!formData.machine_id} // disable until machine selected
                >
                  {locations.map((loc) => (
                    <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                  ))}
                </TextField>
              </Field>
            </Grid>

            {/* Waste Type */}
            <Grid item xs={8}>
              <Field label="Waste Type" required>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={formData.waste_type_id || ""}
                  onChange={(e) => handleChange("waste_type_id", e.target.value)}
                >
                  {wasteTypes.map((w) => (
                    <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
                  ))}
                </TextField>
              </Field>
            </Grid>

            {/* Shift */}
            <Grid item xs={8}>
              <Field label="Shift" required>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={formData.shift_id || ""}
                  onChange={(e) => handleChange("shift_id", e.target.value)}
                >
                  {shiftList.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </TextField>
              </Field>
            </Grid>

            {/* Shift Incharge */}
            <Grid item xs={8}>
              <Field label="Shift Incharge" required>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={formData.shift_incharge_id || ""}
                  onChange={(e) => handleChange("shift_incharge_id", e.target.value)}
                >
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </TextField>
              </Field>
            </Grid>

            {/* Batch No */}
            <Grid item xs={8}>
              <Field label="Batch No/Lot No/Ref.No">
                <TextField
                  size="small"
                  fullWidth
                  value={formData.batch_no || ""}
                  onChange={(e) => handleChange("batch_no", e.target.value)}
                />
              </Field>
            </Grid>

            {/* Reel No */}
            <Grid item xs={8}>
              <Field label="Reel No">
                <TextField
                  size="small"
                  fullWidth
                  value={formData.reel_no || ""}
                  onChange={(e) => handleChange("reel_no", e.target.value)}
                />
              </Field>
            </Grid>

            {/* Quantity */}
            <Grid item xs={8}>
              <Field label="Quantity" required>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.quantity || ""}
                  onChange={(e) => handleChange("quantity", e.target.value)}
                />
              </Field>
            </Grid>

            {/* UOM */}
            <Grid item xs={8}>
              <Field label="UOM" required>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={formData.uom || "KG"}
                  onChange={(e) => handleChange("uom", e.target.value)}
                >
                  <MenuItem value="KG">KG</MenuItem>
                  <MenuItem value="TON">TON</MenuItem>
                </TextField>
              </Field>
            </Grid>

            {/* Remarks */}
            <Grid item xs={8}>
              <Field label="Remarks">
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  value={formData.remarks || ""}
                  onChange={(e) => handleChange("remarks", e.target.value)}
                />
              </Field>
            </Grid>

            {/* Status */}
            <Grid item xs={8}>
              <Box display="flex" alignItems="center" gap={2}>
                <Switch
                  checked={formData.status === undefined ? true : formData.status}
                  onChange={(e) => handleChange("status", e.target.checked)}
                />
                <Typography>{formData.status === false ? "Not Approved" : "Approved"}</Typography>
              </Box>
            </Grid>

          </Grid>
        </Box>
      </Box>

      {/* Alert Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={2000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={alertSeverity}>{alertMessage}</Alert>
      </Snackbar>
    </>
  );
};

// Small reusable label-wrapper
const Field = ({ label, required, children }) => (
  <Box display="grid" gridTemplateColumns="2fr 5fr" gap={2}>
    <Typography fontWeight={600}>
      {label} {required && <span style={{ color: "red" }}>*</span>}
    </Typography>
    {children}
  </Box>
);

export default RepulpWasteEntry;
